<template>
  <form @submit.prevent="saveForm()">
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head flex justify-between">
        <p v-if="duplicate" class="modal-card-title">
          Duplicate Assignment
        </p>
        <p v-else class="modal-card-title">
          {{ assignment ? 'Update Assignment' : 'Add Assignment' }}
        </p>
      </header>
      <section class="modal-card-body">
          <p v-if="duplicate" class="modal-card-title my-3">
              All of the information and sub-tasks for this assignment will be duplicated unless you change it.
              You can change who the assignment should be assigned to, the reviewer, and the due date as needed
          </p>

          <d-field label="Name*">
              <d-input v-model="assignmentForm.name" :required="true" />
          </d-field>
          <d-field label="Due Date*" :required="true">
              <d-datepicker v-model="assignmentForm.dueDate" icon="calendar" expanded />
          </d-field>
          <d-field>
              <d-checkbox v-model="assignmentForm.todosAutoClose">
                  Tasks Auto Close at their Due Date
              </d-checkbox>
          </d-field>
          <d-field>
              <d-checkbox v-model="assignmentForm.optional">
                  Optional Assignment
              </d-checkbox>
          </d-field>
          <d-field label="Category*">
              <d-select v-model="assignmentForm.categoryId" placeholder="Select one..." required>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                      {{ category.name }}
                  </option>
              </d-select>
          </d-field>


          <d-field>
              <d-checkbox v-model="assignmentForm.published">
                  Published
              </d-checkbox>
          </d-field>
          <p class="mb-3">
              Assignment will not be visible to assignees until it is published. Notifications are sent when assignment is published.
          </p>

          <d-field>
              <d-checkbox v-model="assignmentForm.reviewersAreMentors">
                  Reviewers are Mentees' Mentors
              </d-checkbox>
          </d-field>
          <d-field>
              <d-checkbox v-model="assignmentForm.reviewersAreConsultants">
                  Reviewers are Mentors' Consultants
              </d-checkbox>
          </d-field>
          <d-field v-if="!assignmentForm.reviewersAreMentors && !assignmentForm.reviewersAreConsultants" label="Reviewer">
              <div class="pt-2 relative mx-auto text-black">
                  <d-autocomplete v-model="assignmentForm.reviewerId"
                                  :data="availableReviewers"
                                  item-key="userId"
                                  icon="search"
                                  placeholder="Type to find a reviewer..."
                                  field="fullName"
                                  @typing="getReviewersDataAsync">
                      <template v-slot="{option}">
                          <p>{{ option.fullName }}</p>
                          <p class="text-sm text-gray-600">
                              {{ option.email }}
                          </p>
                      </template>
                      <template slot="empty">
                          No results found
                      </template>
                  </d-autocomplete>
              </div>
          </d-field>
          <d-field label="Content">
              <vue-editor v-model="assignmentForm.htmlContent" :editor-toolbar="customToolbar" />
          </d-field>
          <d-field>
              <d-checkbox v-model="assignmentForm.completableByReviewerOnly">
                  Assignment Completable by Reviewer
              </d-checkbox>
              <d-checkbox v-model="assignmentForm.todosCompletableByReviewerOnly">
                  Tasks Completable by Reviewer
              </d-checkbox>
          </d-field>
          <d-field label="Assign To (either a user or a group is required)">
              <div class="flex flex-col">
                  <d-field label="Group">
                      <div class="pt-2 relative mx-auto text-black">
                          <d-autocomplete v-model="assignmentForm.groupId"
                                          icon="search"
                                          :data="availableGroups"
                                          item-key="id"
                                          placeholder="Select a group..."
                                          field="name"
                                          position="top"
                                          @select="group => setUserToNull(group)"
                                          @typing="getGroupsDataAsync">
                              <template v-slot="{option}">
                                  <p>{{ option.name }}</p>
                                  <p class="text-sm text-gray-600">
                                      Group/Community
                                  </p>
                              </template>
                              <template slot="empty">
                                  No results found
                              </template>
                          </d-autocomplete>
                      </div>
                  </d-field>
                  <d-field label="User">
                      <div class="pt-2 relative mx-auto text-black">
                          <d-autocomplete v-model="assignmentForm.userId"
                                          :data="availableUsers"
                                          item-key="userId"
                                          icon="search"
                                          placeholder="Select a user..."
                                          field="fullName"
                                          position="top"
                                          @select="user => setGroupToNull(user)"
                                          @typing="getUsersDataAsync">
                              <template v-slot="{option}">
                                  <p>{{ option.fullName }}</p>
                                  <p class="text-sm text-gray-600">
                                      {{ option.email }}
                                  </p>
                              </template>
                              <template slot="empty">
                                  No results found
                              </template>
                          </d-autocomplete>
                      </div>
                  </d-field>
              </div>
          </d-field>
      </section>
      <footer class="modal-card-foot justify-end">
        <d-btn flat @click="$parent.close()">
          Cancel
        </d-btn>
        <d-btn :loading="saving" type="submit">
          {{ assignment ? 'Update Assignment' : 'Add Assignment' }}
        </d-btn>
      </footer>
    </div>
  </form>
</template>
<script>

  import axios from 'axios';
  import debounce from 'lodash/debounce';
  import dayjs from 'dayjs'
  import utc from 'dayjs/plugin/utc';
  import timezone from 'dayjs/plugin/timezone';

  dayjs.extend(utc);
  dayjs.extend(timezone);

  export default {
    props: {
      assignment: {
        type: Object,
        default: () => {},
      },
      duplicate: {
        type: Boolean,
        default: () => false,
      },
    },
    data: () => ({
      saving: false,
      combinedSearch: '',
      selectedValue: '',
      availableAssignees: [],
      availableReviewers: [],
      availableUsers: [],
      availableGroups: [],
      categories: [],
      isFetching: false,
      assignmentForm: {
        name: '',
        htmlContent: '',
        todosCompletableByReviewerOnly: false,
        completableByReviewerOnly: false,
        dueDate: null,
        userId: '',
        reviewerId: null,
        groupId: null,
        categoryId: null,
        reviewersAreMentors: false,
        reviewersAreConsultants: false,
        todosAutoClose: false,
        optional: false,
        published: false
      },
      customToolbar: [
        [{ header: [false, 3, 4, 5, 6] }], ['bold', 'italic', 'underline', 'strike'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }], ['blockquote', 'code-block'],
        [{ indent: '-1' }, { indent: '+1' }], [{ color: [] }, { background: [] }], ['clean'],
        ['link'],
      ],
    }),
    mounted() {
      if (this.assignment) {
        this.getGroupsDataAsync(this.assignment.groupName);
        this.getReviewersDataAsync(this.assignment.reviewerFullName);
        this.getUsersDataAsync(this.assignment.userFullName);
        this.assignmentForm = JSON.parse(JSON.stringify(this.assignment));
        if (this.duplicate) {
          this.assignmentForm.name = `${this.assignmentForm.name} COPY`;
        }
      }
      this.getCategoryData();
    },

    methods: {
      async saveForm() {
        this.saving = true;
        if (this.assignmentForm.reviewersAreMentors) {
          this.assignmentForm.reviewerId = null;
        }
        try {
          if (this.assignment) {
            this.assignmentForm.dueDate = this.formatAsDatetimeWithOffset(this.assignmentForm.dueDate);
            await axios.patch(`/api/admin/assignments/${this.assignmentForm.id}?duplicate=${this.duplicate}`, this.assignmentForm);
          } else {
            this.assignmentForm.dueDate = this.formatAsDatetimeWithOffset(this.assignmentForm.dueDate);
            await axios.post('/api/admin/assignments/', this.assignmentForm);
          }
          this.$toast.open({ message: 'Save successful!', color: 'green-500', duration: 4000 });

          await this.$parent.events.getAssignmentsPaged();
          this.$parent.close();
        } catch (error) {
          this.$toast.error(error.response.data[0]);
        } finally {
          this.saving = false;
        }
      },

      setUserToNull(group) {
        this.assignmentForm.groupId = group.id;
        this.assignmentForm.userId = null;
      },

      setGroupToNull(user) {
        this.assignmentForm.userId = user.id;
        this.assignmentForm.groupId = null;
      },
      async getCategoryData() {
        const { data } = await axios.get('/api/admin/assignment-categories/paged?currentPage=1&pageSize=9999&sortDir=name asc');
        this.categories = data.resultItems;
      },

      formatAsDatetimeWithOffset(date) {
        const datetime = dayjs(date);
        const offset = datetime.utcOffset();
        const offsetHours = Math.floor(Math.abs(offset) / 60);
        const offsetMinutes = Math.abs(offset) % 60;
        const offsetSign = offset < 0 ? "-" : "+";
        const formattedOffset = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
        const formattedDatetimeWithOffset = datetime.format('YYYY-MM-DD HH:mm:ss') + `${formattedOffset}`;

        return formattedDatetimeWithOffset;
      },

      getGroupsDataAsync: debounce(async function (name = '') {
        this.isFetching = true;
        try {
          const { data } = await axios.get(`/api/group-lookup?filter=${name}`);
          this.availableGroups = data;
        } catch (error) {
          this.availableGroups = [];
          throw error;
        } finally {
          this.isFetching = false;
        }
      }, 500),

      getUsersDataAsync: debounce(async function (name = '') {
        this.isFetching = true;
        try {
          const { data } = await axios.get(`/api/user-lookup?filter=${name}`);
          this.availableUsers = data;
        } catch (error) {
          this.availableUsers = [];
          throw error;
        } finally {
          this.isFetching = false;
        }
      }, 500),

      getReviewersDataAsync: debounce(async function (name = '') {
        this.isFetching = true;
        try {
          const { data } = await axios.get(`/api/user-lookup?filter=${name}`);
          this.availableReviewers = data;
        } catch (error) {
          this.availableReviewers = [];
          throw error;
        } finally {
          this.isFetching = false;
        }
      }, 500),

    },

  };
</script>